"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NavigationService = void 0;
exports.createNavigationService = createNavigationService;
/*
 * Copyright 2025, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const shuffle_1 = __importDefault(require("lodash/shuffle"));
const set_1 = __importDefault(require("lodash/set"));
const NavigationTypes_1 = require("../types/NavigationTypes");
const navigationHelpers_1 = require("./navigationHelpers");
const helpers_1 = require("../../../utils/helpers");
const helpers_2 = require("../../odSurvey/helpers");
/**
 * NavigationService handles complex navigation for questionnaires,
 * including repeated blocks of sections and conditional navigation.
 */
class NavigationService {
    constructor(sections) {
        // If the section is one with a repeated block, get the actual next section
        // from this block. Target section is the one who may be a repeated block
        // and origin section is the origin of the navigation. They may not be
        // identical if some sections were skipped in between
        this.maybeGetTargetFromRepeatedBlock = ({ interview, targetSection, originSection, direction }) => {
            const targetSectionConfig = this.sections[targetSection.sectionShortname];
            if (targetSectionConfig.type === 'repeatedBlock') {
                return this.getTargetSectionFromRepeatedBlock({
                    interview,
                    originSection,
                    repeatedBlockSection: targetSectionConfig,
                    direction
                });
            }
            return [targetSection, undefined];
        };
        this.sections = sections;
    }
    // Get the first section, the one that has no previous
    getFirstSection() {
        const firstSection = Object.keys(this.sections).find((sectionName) => this.sections[sectionName].previousSection === null);
        if (firstSection === undefined) {
            throw new Error('No first section found');
        }
        return firstSection;
    }
    logAndThrowErrorInSectionFunction({ section, functionName, error }) {
        const message = `NavigationService: Error evaluating ${functionName} for section ${section.sectionShortname}${section.iterationContext ? ` with iteration ${section.iterationContext.join('/')}` : ''}: ${error}`;
        console.error(message);
        throw new Error(message);
    }
    // Determine if a section is enabled
    isSectionEnabled({ interview, section }) {
        const sectionConfig = this.sections[section.sectionShortname];
        if (sectionConfig.enableConditional) {
            try {
                return (0, helpers_1.parseBoolean)(sectionConfig.enableConditional, interview, section.sectionShortname);
            }
            catch (error) {
                this.logAndThrowErrorInSectionFunction({ section, functionName: 'enableConditional', error });
            }
        }
        // By default, the section is enabled if the previous section is completed
        return sectionConfig.previousSection === null
            ? true
            : this.isSectionCompleted({
                interview,
                section: this.handleIterationContextOnSection({
                    targetSection: {
                        sectionShortname: sectionConfig.previousSection,
                        iterationContext: section.iterationContext
                    },
                    interview
                })
            });
    }
    isSectionCompleted({ interview, section }) {
        // TODO Should we check validity of current section widgets? If so, we'd
        // need to make sure to be able to calculate them for sections that were
        // not active. Maybe not...
        const sectionConfig = this.sections[section.sectionShortname];
        // Run the section's `isSectionCompleted` function if it exists
        if (sectionConfig.isSectionCompleted) {
            try {
                return sectionConfig.isSectionCompleted(interview, section.iterationContext);
            }
            catch (error) {
                this.logAndThrowErrorInSectionFunction({ section, functionName: 'isSectionCompleted', error });
            }
        }
        // If there is no `isSectionCompleted` function, it is considered
        // completed for navigation. Any validation should be done in the
        // section itself.
        return true;
    }
    // Verify that a section is both enabled and visible. Enable and visible are
    // different from the point of view of navigation menu, but they are the
    // same for navigation.
    isSectionEnabledAndVisible({ interview, section }) {
        if (!this.isSectionEnabled({ interview, section })) {
            return false;
        }
        const sectionConfig = this.sections[section.sectionShortname];
        // If the section is the selection section of a repeated block, make it invisible if there is only one iteration
        if (sectionConfig.type === 'section' && sectionConfig.repeatedBlockSection) {
            const blockSection = this.sections[sectionConfig.repeatedBlockSection];
            if (blockSection.repeatedBlock.selectionSectionId === section.sectionShortname) {
                const [iterationContexts] = this.getIterationContextsForBlock({ interview, blockSection });
                return iterationContexts.length > 1;
            }
        }
        // Run the section's `isSectionVisible` function if it exists
        if (sectionConfig.isSectionVisible) {
            try {
                return sectionConfig.isSectionVisible(interview, section.iterationContext);
            }
            catch (error) {
                this.logAndThrowErrorInSectionFunction({ section, functionName: 'isSectionVisible', error });
            }
        }
        // By default the section is visible
        return true;
    }
    // Find the first section that shouldn't be skipped in the given direction
    findFirstNonSkippedSection({ interview, currentSection, direction }) {
        // Section visited in the current iteration, can be disabled or not visible
        let lastVisitedSection = currentSection;
        // Section to navigate to, must be visible and enabled
        const navigateToSection = currentSection;
        // visited set to prevent infinite loops.
        const visited = new Set();
        // Check for infinite loop
        while (!visited.has((0, NavigationTypes_1.sectionToUrlPath)(lastVisitedSection))) {
            visited.add((0, NavigationTypes_1.sectionToUrlPath)(lastVisitedSection));
            // Get the next section in the direction
            const sectionConfig = this.sections[lastVisitedSection.sectionShortname];
            const targetSectionName = direction === 'forward' ? sectionConfig.nextSection : sectionConfig.previousSection;
            // If null, keep current section
            if (targetSectionName === null) {
                return [navigateToSection];
            }
            // Get the target section and add the iteration context if necessary
            const targetSection = this.handleIterationContextOnSection({
                targetSection: {
                    sectionShortname: targetSectionName,
                    iterationContext: lastVisitedSection.iterationContext
                },
                interview
            });
            // See if it is possible to go to this section. If not,
            // continue navigation.
            if (!this.isSectionEnabledAndVisible({ interview, section: targetSection })) {
                lastVisitedSection = targetSection;
                continue;
            }
            const [targetSectionWithRepeated, updatedValues] = this.maybeGetTargetFromRepeatedBlock({
                interview,
                targetSection,
                originSection: lastVisitedSection,
                direction
            });
            // If the target section in repeated block is different from original target section, verify visibility again.
            if (!(0, isEqual_1.default)(targetSectionWithRepeated, targetSection) &&
                !this.isSectionEnabledAndVisible({ interview, section: targetSectionWithRepeated })) {
                lastVisitedSection = targetSectionWithRepeated;
                continue;
            }
            // Return the section
            return [targetSectionWithRepeated, updatedValues];
        }
        console.error(`NavigationService: Infinite loop detected in 'findFirstNonSkippedSection'. Will return current section ${(0, NavigationTypes_1.sectionToUrlPath)(lastVisitedSection)}, but it may not be right.`);
        return [navigateToSection];
    }
    // Get the list of contexts for the repeated block that will need to be
    // iterated over. The values to update are any randomization values.
    getIterationContextsForBlock({ interview, blockSection }) {
        const interviewFieldsToUpdate = {};
        // Get the object ids from repeatForObjects
        const getObjects = () => {
            if (blockSection.repeatedBlock.iterationRule.type === 'builtin') {
                if (blockSection.repeatedBlock.iterationRule.path === 'interviewablePersons') {
                    return (0, helpers_2.getInterviewablePersonsArray)({ interview }).map((person) => person._uuid);
                }
            }
            else if (blockSection.repeatedBlock.iterationRule.type === 'surveyObjectPath') {
                const surveyObjects = (0, helpers_1.getResponse)(interview, blockSection.repeatedBlock.iterationRule.path, {});
                return Object.keys(surveyObjects);
            }
            throw new Error(`Unsupported repeatForObjects type: ${blockSection.repeatedBlock.iterationRule}`);
        };
        let objectIds = getObjects();
        // See if we need to randomize
        if (blockSection.repeatedBlock.order === 'random') {
            // See if we already have a random order
            const randomOrderPath = `_${blockSection.repeatedBlock.pathPrefix || ''}RandomSequence`;
            const randomOrder = (0, helpers_1.getResponse)(interview, randomOrderPath, []);
            // If the random order is not set or if it is not valid anymore, set it right
            if (randomOrder === undefined ||
                randomOrder.length !== objectIds.length ||
                randomOrder.some((id) => !objectIds.includes(id))) {
                objectIds = (0, shuffle_1.default)(objectIds);
                interviewFieldsToUpdate[`response.${randomOrderPath}`] = objectIds;
            }
            else {
                // Otherwise, use the random order, don't use the array from the response directly, copy it instead
                objectIds = [...randomOrder];
            }
        }
        return [
            objectIds.map((id) => blockSection.repeatedBlock.pathPrefix ? [blockSection.repeatedBlock.pathPrefix, id] : [id]),
            Object.keys(interviewFieldsToUpdate).length === 0 ? undefined : interviewFieldsToUpdate
        ];
    }
    findFirstVisibleSectionInRepeatedBlock({ interview, repeatedBlockSection, iterationContext, startFrom }) {
        // Find the first visible section in the repeated block starting from
        // the given section. If no section is found, return the first one.
        const sections = repeatedBlockSection.repeatedBlock.sections;
        const startIndex = sections.indexOf(startFrom);
        if (startIndex === -1) {
            return startFrom;
        }
        // Check visiblity of the section before returning, as otherwise the
        // initNaviagion will navigate backward, thus out of the block/iteration
        for (let i = startIndex; i < sections.length; i++) {
            const sectionName = sections[i];
            if (this.isSectionEnabledAndVisible({
                interview,
                section: {
                    sectionShortname: sectionName,
                    iterationContext
                }
            })) {
                return sectionName;
            }
        }
        // If no visible section found, return the first section, the caller will handle the invisibility depending on the navigation direction.
        return startFrom;
    }
    findTargetSectionInRepeatedBlock({ interview, repeatedBlockSection, iterationContext }) {
        // There are 2 flow of possibilities for the target section of the block
        // 1. If it is the natural flow (the new iteration has not started yet),
        //    then we go to the first section of the block, or skip it if it is
        //    the selection section and the block is configured to skip in
        //    natural flow
        // 2. If not in natural flow, go to the selection section if specified
        //    or to the first one of the block
        const isNaturalFlow = !(0, navigationHelpers_1.isIterationContextStarted)({ interview, iterationContext });
        const firstBlockSection = repeatedBlockSection.repeatedBlock.sections[0];
        if (isNaturalFlow) {
            return this.findFirstVisibleSectionInRepeatedBlock({
                interview,
                repeatedBlockSection,
                iterationContext,
                startFrom: repeatedBlockSection.repeatedBlock.skipSelectionInNaturalFlow &&
                    firstBlockSection === repeatedBlockSection.repeatedBlock.selectionSectionId
                    ? repeatedBlockSection.repeatedBlock.sections[1]
                    : firstBlockSection
            });
        }
        else {
            // If not in natural flow, go to the selection section if specified
            return this.findFirstVisibleSectionInRepeatedBlock({
                interview,
                repeatedBlockSection,
                iterationContext,
                startFrom: repeatedBlockSection.repeatedBlock.selectionSectionId
                    ? repeatedBlockSection.repeatedBlock.selectionSectionId
                    : firstBlockSection
            });
        }
    }
    deactivateActiveSurveyObject({ repeatedBlockSection, valuesByPath }) {
        return Object.assign(valuesByPath || {}, {
            [`response.${repeatedBlockSection.repeatedBlock.activeSurveyObjectPath}`]: undefined
        });
    }
    getTargetSectionFromRepeatedBlock({ interview, originSection, repeatedBlockSection, direction }) {
        // Get the iteration contexts for which the block will be repeated
        const [iterationContexts, updatedValues] = this.getIterationContextsForBlock({
            interview,
            blockSection: repeatedBlockSection
        });
        // Get the index of the current iteration in the iteration contexts
        const currentIterationIndex = iterationContexts.findIndex((context) => (0, isEqual_1.default)(context, originSection.iterationContext));
        // See if we need to get off the block if current iteration is the last
        // and the direction is fioward, or if the iteration is the first and
        // direction is backward.
        if (currentIterationIndex === iterationContexts.length - 1 && direction === 'forward') {
            // If the last iteration is completed, go to the next section, or first invalid iteration
            return this.findEndOfBlockNextSection({
                interview,
                repeatedBlockSection,
                iterationContexts,
                updatedValues
            });
        }
        else if (currentIterationIndex === 0 && direction === 'backward' && repeatedBlockSection.previousSection) {
            return [
                { sectionShortname: repeatedBlockSection.previousSection },
                this.deactivateActiveSurveyObject({ repeatedBlockSection, valuesByPath: updatedValues })
            ];
        }
        // Determine the next object to activate: if there was no previously
        // selected object, take the first, otherwise, take the next one in the
        // direction of the navigation
        const nextIterationIndex = currentIterationIndex === -1
            ? 0
            : Math.max(0, Math.min(currentIterationIndex + (direction === 'forward' ? 1 : -1), iterationContexts.length - 1));
        const nextIterationContext = iterationContexts[nextIterationIndex];
        // Go to the target section of the repeated block
        const targetSectionName = this.findTargetSectionInRepeatedBlock({
            interview,
            repeatedBlockSection,
            iterationContext: nextIterationContext
        });
        return [{ sectionShortname: targetSectionName, iterationContext: nextIterationContext }, updatedValues];
    }
    findEndOfBlockNextSection({ interview, repeatedBlockSection, iterationContexts, updatedValues }) {
        // The navigation in the repeated block section may not have been linear
        // and some iterations might be invalid. We find the first invalid
        // iteration and navigate to the first visible section of this block.
        if (repeatedBlockSection.repeatedBlock.isIterationValid !== undefined) {
            const firstInvalidIteration = iterationContexts.find((context) => {
                try {
                    return !repeatedBlockSection.repeatedBlock.isIterationValid(interview, context);
                }
                catch (error) {
                    this.logAndThrowErrorInSectionFunction({
                        section: { sectionShortname: repeatedBlockSection.sectionName, iterationContext: context },
                        functionName: 'isIterationValid',
                        error
                    });
                }
            });
            if (firstInvalidIteration) {
                // If there is an invalid iteration, find first visible section
                // in the block for this person, skipping the selection section
                // if required
                return [
                    {
                        sectionShortname: this.findFirstVisibleSectionInRepeatedBlock({
                            interview,
                            repeatedBlockSection,
                            iterationContext: firstInvalidIteration,
                            startFrom: repeatedBlockSection.repeatedBlock.selectionSectionId
                                ? repeatedBlockSection.repeatedBlock.sections[1]
                                : repeatedBlockSection.repeatedBlock.sections[0]
                        }),
                        iterationContext: firstInvalidIteration
                    },
                    updatedValues
                ];
            }
        }
        return [
            { sectionShortname: repeatedBlockSection.nextSection },
            this.deactivateActiveSurveyObject({ repeatedBlockSection, valuesByPath: updatedValues })
        ];
    }
    // Add the iteration context to the target section or remove any previous
    // context if it is not in a repeated block
    goToCurrentIterationContextOrSelect({ interview, sectionName, repeatedBlockSectionName }) {
        const sectionConfig = this.sections[repeatedBlockSectionName];
        if (sectionConfig.type !== 'repeatedBlock') {
            return {
                sectionShortname: sectionName,
                iterationContext: undefined
            };
        }
        const repeatedBlock = sectionConfig.repeatedBlock;
        // Get the current active object in the repeated block
        const activeObjectId = (0, helpers_1.getResponse)(interview, repeatedBlock.activeSurveyObjectPath);
        if (activeObjectId === undefined) {
            return {
                sectionShortname: repeatedBlock.selectionSectionId || repeatedBlockSectionName,
                iterationContext: undefined
            };
        }
        else {
            return {
                sectionShortname: sectionName,
                iterationContext: repeatedBlock.pathPrefix
                    ? [repeatedBlock.pathPrefix, activeObjectId]
                    : [activeObjectId]
            };
        }
    }
    // Set the section as completed
    setSectionCompleted({ interview, section }) {
        // FIXME Not sure this is the place to set this, but since some enable
        // conditional may depend on preceding section's completion status, we
        // need to know that a section is completed. Using _set directly is not
        // the best way, see if we should add a helper for this is we need to
        // set completion of a section more generally. The backend also has
        // similar code. Eventually, when implementing
        // https://github.com/chairemobilite/evolution/issues/969, the section
        // navigation history and completion won't be stored in the responses
        // anymore and we'll have a better way to set it, so we can go for the
        // easy fix for now.
        (0, set_1.default)(interview, `response._sections.${section.sectionShortname}${section.iterationContext ? '.' + section.iterationContext.join('/') : ''}._isCompleted`, true);
    }
    _navigate(interview, currentSection, direction = 'forward') {
        const correctedCurrentSection = this.correctCurrentSectionIteration({ interview, currentSection });
        // Find the first non-skipped section in the requested direction
        const [targetSection, valuesByPath] = this.findFirstNonSkippedSection({
            interview,
            currentSection: correctedCurrentSection,
            direction
        });
        // return current state if next section is the same as the current, otherwise, return the new state
        const valuesToUpdate = this.getValuesToUpdate({ interview, targetSection });
        return (0, isEqual_1.default)(targetSection, currentSection)
            ? { targetSection: currentSection }
            : {
                targetSection,
                valuesByPath: valuesByPath === undefined ? valuesToUpdate : Object.assign(valuesByPath, valuesToUpdate)
            };
    }
    /**
     * Main navigation function to determine the target section in the
     * appropriate direction. If the navigation is not possible in the requested
     * direction, the `TargetSectionResult` object will return the current
     * navigation state.
     *
     * Note that the navigation service cannot determine if a form content is
     * valid, so if the navigation is called, it means there are no invalid
     * widgets, or navigation can take place. It will use the section's
     * configuration and `isSectionCompleted` and `isSectionVisible` functions
     * to determine respectively completion and visibility of a section.
     *
     * @param {Object} params The parameters for the navigation
     * @param {UserRuntimeInterviewAttributes} params.interview The interview for which
     * to navigate
     * @param {NavigationSection} params.currentSection The current navigation section
     * @param {'forward'|'backward'} [params.direction] The direction to navigate,
     * either 'forward' or 'backward'. Defaults to `forward`
     * @returns The next section to visit and the updated navigation state
     */
    navigate({ interview, currentSection, direction = 'forward' }) {
        // If direction is 'forward', validate that the current section is valid and completed. If not, return current section
        if (direction === 'forward') {
            if (!this.isSectionCompleted({ interview, section: currentSection })) {
                return {
                    targetSection: currentSection
                };
            }
            else {
                this.setSectionCompleted({ interview, section: currentSection });
            }
        }
        return this.handleSectionEntryExit({
            interview,
            targetSection: this._navigate(interview, currentSection, direction),
            previousSection: currentSection
        });
    }
    // If the current section is the survey object selection section, update the current iteration to match the
    // selected object. This is used to correct the current section iteration context when navigating to a section
    // that is in a repeated block.
    correctCurrentSectionIteration({ interview, currentSection }) {
        const currentSectionConfig = this.sections[currentSection.sectionShortname];
        if (currentSectionConfig.type === 'section' && currentSectionConfig.repeatedBlockSection) {
            const repeatedBlockConfig = this.sections[currentSectionConfig.repeatedBlockSection];
            // If the section is not the selection section, no need for any correction
            if (repeatedBlockConfig.repeatedBlock.selectionSectionId === currentSection.sectionShortname) {
                // Just go to the current iteration of the section and return it if there is one, otherwise, keep the current section
                const correctedCurrentSection = this.goToCurrentIterationContextOrSelect({
                    interview,
                    sectionName: currentSection.sectionShortname,
                    repeatedBlockSectionName: currentSectionConfig.repeatedBlockSection
                });
                if (correctedCurrentSection.iterationContext !== undefined) {
                    return correctedCurrentSection;
                }
            }
        }
        return currentSection;
    }
    // Add the iteration context to the target section if it is in a repeated
    // block and not set yet, or reversely, remove it if it was set and not in a
    // repeated block section
    handleIterationContextOnSection({ targetSection, interview }) {
        const sectionConfig = this.sections[targetSection.sectionShortname];
        if (targetSection.iterationContext === undefined &&
            sectionConfig.type === 'section' &&
            sectionConfig.repeatedBlockSection) {
            return this.goToCurrentIterationContextOrSelect({
                interview,
                sectionName: targetSection.sectionShortname,
                repeatedBlockSectionName: sectionConfig.repeatedBlockSection
            });
        }
        else if (targetSection.iterationContext !== undefined &&
            sectionConfig.type === 'section' &&
            !sectionConfig.repeatedBlockSection) {
            // Remove the iteration context if it is not in a repeated block
            return { sectionShortname: targetSection.sectionShortname, iterationContext: undefined };
        }
        return targetSection;
    }
    // Add the section entry and exit functions side effect to the target result
    handleSectionEntryExit({ interview, targetSection, previousSection }) {
        // Execute the `onSectionExit` of the previous section
        const previousSectionConfig = previousSection ? this.sections[previousSection.sectionShortname] : undefined;
        const wrapSectionExit = (previousSectionConfig) => {
            try {
                return previousSectionConfig.onSectionExit(interview, previousSection === null || previousSection === void 0 ? void 0 : previousSection.iterationContext);
            }
            catch (error) {
                this.logAndThrowErrorInSectionFunction({
                    section: previousSection,
                    functionName: 'onSectionExit',
                    error
                });
            }
        };
        const sectionExitValues = previousSectionConfig && previousSectionConfig.onSectionExit
            ? wrapSectionExit(previousSectionConfig)
            : undefined;
        // Execute the `onSectionEntry` of the target section
        const sectionConfig = this.sections[targetSection.targetSection.sectionShortname];
        const wrapSectionEntry = (sectionConfig) => {
            try {
                return sectionConfig.onSectionEntry(interview, targetSection.targetSection.iterationContext);
            }
            catch (error) {
                this.logAndThrowErrorInSectionFunction({
                    section: targetSection.targetSection,
                    functionName: 'onSectionEntry',
                    error
                });
            }
        };
        const sectionEntryValues = sectionConfig.onSectionEntry ? wrapSectionEntry(sectionConfig) : undefined;
        // Merge the values from both section entry and exit functions with the valuesByPath of targetSection
        const updatedValues = Object.assign({}, targetSection.valuesByPath, sectionExitValues, sectionEntryValues);
        return {
            targetSection: targetSection.targetSection,
            valuesByPath: Object.keys(updatedValues).length > 0 ? updatedValues : undefined
        };
    }
    getValuesToUpdate({ interview, targetSection }) {
        if (targetSection.iterationContext !== undefined) {
            // Make sure the proper iteration context is set in the interview
            const sectionConfig = this.sections[targetSection.sectionShortname];
            const repeatedBlock = sectionConfig.type === 'section' && sectionConfig.repeatedBlockSection
                ? this.sections[sectionConfig.repeatedBlockSection]
                    .repeatedBlock
                : sectionConfig.repeatedBlock;
            if (repeatedBlock) {
                // Get the current active object in the repeated block
                const activeObjectId = (0, helpers_1.getResponse)(interview, repeatedBlock.activeSurveyObjectPath);
                if (activeObjectId !== targetSection.iterationContext[targetSection.iterationContext.length - 1]) {
                    // Need to update the active object in the interview
                    return {
                        [`response.${repeatedBlock.activeSurveyObjectPath}`]: targetSection.iterationContext[targetSection.iterationContext.length - 1]
                    };
                }
            }
        }
        return undefined;
    }
    _initNavigationState({ interview, requestedSection }) {
        // Find the target section: either the requested section or last visited
        // section in the interview, or the first one
        const getTargetSection = () => {
            if (requestedSection && this.sections[requestedSection]) {
                return { sectionShortname: requestedSection };
            }
            const lastVisitedSection = (0, navigationHelpers_1.getLastVisitedSection)({ interview });
            if (lastVisitedSection && this.sections[lastVisitedSection.sectionShortname]) {
                return lastVisitedSection;
            }
            return { sectionShortname: this.getFirstSection() };
        };
        const targetSection = getTargetSection();
        // Find the iteration context for the target section if necessary
        const targetSectionWithContext = this.handleIterationContextOnSection({ targetSection, interview });
        // TODO Make sure all the sections prior to the requested one are valid and complete
        // See if it is possible to go to this section. If so, return the section
        if (this.isSectionEnabledAndVisible({ interview, section: targetSectionWithContext })) {
            const [targetSectionWithRepeated, updatedValues] = this.maybeGetTargetFromRepeatedBlock({
                interview,
                targetSection: targetSectionWithContext,
                originSection: targetSectionWithContext,
                direction: 'forward'
            });
            if ((0, isEqual_1.default)(targetSectionWithRepeated, targetSectionWithContext) ||
                this.isSectionEnabledAndVisible({ interview, section: targetSectionWithRepeated })) {
                const finalUpdatedValues = this.getValuesToUpdate({
                    interview,
                    targetSection: targetSectionWithRepeated
                });
                return {
                    targetSection: targetSectionWithRepeated,
                    valuesByPath: updatedValues === undefined
                        ? finalUpdatedValues
                        : Object.assign({}, finalUpdatedValues, updatedValues)
                };
            }
        }
        // Otherwise, find the first incomplete section backwards
        const [targetSectionNonSkipped, valuesByPath] = this.findFirstNonSkippedSection({
            interview,
            currentSection: targetSectionWithContext,
            direction: 'backward'
        });
        const valuestoUpdate = this.getValuesToUpdate({ interview, targetSection: targetSectionNonSkipped });
        return {
            targetSection: targetSectionNonSkipped,
            valuesByPath: valuesByPath === undefined ? valuestoUpdate : Object.assign(valuesByPath, valuestoUpdate)
        };
    }
    /**
     * Go to a specific section or initialize a new navigation state. This can
     * either happen when the page is first loaded to go to the last section, or
     * when the user requests a specific section, for example through the URL or
     * by clicking the navigation menu. In any case, it will not navigate to a
     * section that is not enabled or accessible yet.
     *
     * @param {Object} params The parameters for the navigation state
     * @param {UserRuntimeInterviewAttributes} params.interview The interview for which
     * to create the navigation state
     * @param {string} [params.requestedSection] The section to navigate to. If left
     * blank, it will either navigate to the last visited (or allowed) section
     * or the first one.
     * @param {NavigationSection} [params.currentSection] The current section, if any.
     */
    initNavigationState({ interview, requestedSection, currentSection }) {
        return this.handleSectionEntryExit({
            interview,
            targetSection: this._initNavigationState({ interview, requestedSection }),
            previousSection: currentSection
        });
    }
}
exports.NavigationService = NavigationService;
/**
 * Create a navigation service instance from survey sections
 *
 * @param {SurveySections} sections The survey sections description for the current survey
 * @returns {NavigationService} The navigation service instance
 */
function createNavigationService(sections) {
    return new NavigationService(sections);
}
//# sourceMappingURL=NavigationService.js.map